import { Button, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrescriptionMedication } from "src/api/types";
import { useIsMobile } from "src/hooks/useIsMobile";
import WhiteCard from "src/shared/components/WhiteCard";
import MedicationInstruction from "src/components/MedicationInstruction";
import SyringeWithUnits from "../SyringeWithUnits";

interface TreatmentPlanCardProps {
  details: PrescriptionMedication;
  page: string;
  isGlp1: boolean;
  numberOfMonths: number;
  category: string;
}

const mapWeeksToVialIndex: string[] = ["1-4", "5-8", "9-12"];
const mapMonthsToInstruction: Record<number, string> = {
  3: "1-3",
  5: "1-5",
  6: "1-6",
};

const TreatmentPlanCard = ({
  page,
  details,
  isGlp1,
  numberOfMonths,
  category,
}: TreatmentPlanCardProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const component = "treatmentPlanCard";

  const [selected, setSelected] = useState<number>(0);

  const handleWeekSelect = useCallback((index: number) => {
    setSelected(index);
  }, []);

  const durationText = useCallback(
    (index: number) => {
      if (
        ["Hair Loss", "Birth Control", "Erectile Dysfunction"].includes(
          category,
        )
      ) {
        return `${t(`${page}.${component}.months`)} ${
          mapMonthsToInstruction[numberOfMonths]
        }`;
      }

      return `${t(`${page}.${component}.weeks`)} ${mapWeeksToVialIndex[index]}`;
    },
    [category, numberOfMonths, page, t],
  );

  return (
    <WhiteCard gap={3}>
      <Typography fontWeight={600} variant="h6">
        {t(`${page}.${component}.title`)}
      </Typography>
      <Stack gap={1}>
        <Typography fontWeight={500}>
          {t(`${page}.${component}.howToTake.header`)}
        </Typography>
        <Typography variant="body2">
          {t(`${page}.${component}.howToTake.subtext`)}
        </Typography>
      </Stack>
      <Stack
        gap={3}
        sx={{
          backgroundColor: "#F2FFFF",
          boxShadow: "0px 3px 10px 0px rgba(0, 0, 0, 0.06)",
          padding: "16px",
        }}
      >
        <Stack
          direction="row"
          gap={1}
          width={
            (details.instructions.length ?? 0 > 1)
              ? "100%"
              : isMobile
                ? "50%"
                : "25%"
          }
        >
          {details.instructions.map((_, idx) => {
            const isSelected = selected === idx;

            return (
              <Button
                key={idx}
                variant={isSelected ? "contained" : "outlined"}
                fullWidth
                sx={{
                  borderRadius: "100px",
                  minHeight: isMobile ? "64px" : "",
                  padding: "6px 16px",
                }}
                onClick={() => handleWeekSelect(idx)}
              >
                {durationText(idx)}
              </Button>
            );
          })}
        </Stack>
        {details != null ? (
          <Stack gap={3}>
            <Stack>
              <MedicationInstruction
                detail={details}
                selected={selected}
                category={category}
              />
            </Stack>
            {isGlp1 ? (
              <SyringeWithUnits detail={details} selected={selected} />
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </WhiteCard>
  );
};

export default TreatmentPlanCard;
